import React, { useState, useEffect } from 'react';
import {
  CardBody,
  Input,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from 'reactstrap';
import { COUNTER_ACCENT } from '../../../../../shared/offer-domain/constants';
import CounterOfferModal from '../CounterOfferModal';
import { getRowKey } from '../../../hooks/modelDeliveryTable/shared';
import {
  formatIncotermRoute,
  formatFobLocationsJoined,
  hasCounterOffer,
  isCounterValueChanged,
  isCounterQuantityChanged,
  isCounterDateChanged,
  isFobOriginDisplay,
  resolveCapableLocationSelections,
} from '../shared/utils';
import {
  cellClass,
  headerCellClass,
  renderPortCell,
  renderSelectedOriginLocations,
  createNumberInputRenderer,
} from '../shared/tableRenderHelpers';

const getTableColSpan = (isDeliveryEditable, usesDyedUndyed) => {
  const alphaCols = usesDyedUndyed ? 2 : 1;
  const base = 9 + alphaCols;
  return isDeliveryEditable ? base + 1 : base;
};

const isFobIncotermRow = data =>
  String(data?.incoterm || '')
    .trim()
    .toUpperCase() === 'FOB';

const isCounterFobIncoterm = data =>
  String(data?.counterIncoterm || '')
    .trim()
    .toUpperCase() === 'FOB';

const getRowOriginLocations = data =>
  (Array.isArray(data?.incotermFOBLocations) ? data.incotermFOBLocations : [])
    .map(loc => ({
      id: String(loc?.id ?? loc?.Id ?? ''),
      name: loc?.name ?? loc?.Name ?? '',
    }))
    .filter(loc => loc.id && loc.name);

const getRowOriginNames = data =>
  getRowOriginLocations(data)
    .map(loc => loc.name)
    .filter(Boolean);

const resolveLoadingPortLabel = (data, selectedOrigins) => {
  if (data?.fobOriginLocationName) {
    return data.fobOriginLocationName;
  }
  if (data?.incotermLocationId) {
    const fromSelected = Array.isArray(selectedOrigins)
      ? selectedOrigins.find(
          loc => String(loc?.id) === String(data.incotermLocationId)
        )
      : null;
    return fromSelected?.name || data?.loadingPort || '-';
  }
  const names = getRowOriginNames(data);
  if (isFobOriginDisplay(data?.route, names)) {
    const selectedNames = Array.isArray(selectedOrigins)
      ? selectedOrigins.map(loc => loc?.name || loc).filter(Boolean)
      : selectedOrigins?.name
        ? [selectedOrigins.name]
        : selectedOrigins
          ? [selectedOrigins]
          : [];
    return renderSelectedOriginLocations(selectedNames);
  }
  if (isFobIncotermRow(data) || names.length) {
    return formatFobLocationsJoined(names) || data?.loadingPort || '-';
  }
  return '-';
};

const SelectOriginLocationModal = ({
  isOpen,
  toggle,
  locations = [],
  selectedIds = [],
  onToggle,
  onConfirm,
}) => {
  const selectedSet = new Set((selectedIds || []).map(String));

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size='md'>
      <ModalHeader
        toggle={toggle}
        className='fw-semibold'
        style={{ borderBottom: '1px solid var(--vz-border-color, #e5e7eb)' }}
      >
        <i className='ri-map-pin-line me-2' style={{ color: '#0d7c66' }} />
        Select Origin Location
      </ModalHeader>
      <ModalBody className='py-4'>
        {locations.length === 0 ? (
          <p
            className='text-muted text-center py-3 mb-0'
            style={{ fontSize: '13px' }}
          >
            No origin locations available
          </p>
        ) : (
          <div
            className='table-responsive'
            style={{ maxHeight: '407px', overflowY: 'auto' }}
          >
            <table
              className='align-middle table-nowrap mb-0'
              style={{ width: '100%' }}
            >
              <thead className='table-light'>
                <tr>
                  <th style={{ width: '50px' }} />
                  <th>Location Name</th>
                </tr>
              </thead>
              <tbody>
                {locations.map(loc => (
                  <tr
                    key={loc.id}
                    style={{
                      borderBottom: '1px solid var(--vz-border-color, #e5e7eb)',
                    }}
                  >
                    <td className='text-center'>
                      <Input
                        type='checkbox'
                        checked={selectedSet.has(String(loc.id))}
                        onChange={() => onToggle(loc)}
                      />
                    </td>
                    <td>{loc.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ModalBody>
      <ModalFooter
        style={{ borderTop: '1px solid var(--vz-border-color, #e5e7eb)' }}
      >
        <Button color='secondary' size='sm' outline onClick={toggle}>
          Cancel
        </Button>
        <Button
          color='primary'
          size='sm'
          disabled={!selectedIds?.length}
          onClick={onConfirm}
        >
          Confirm
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const getIncotermGroupKey = (data, index) =>
  String(data?.quantityIncotermId ?? `row-${index}`);

const ModelDeliveryTableNonLpg = ({
  embedded = false,
  isDeliveryEditable,
  isLoading = false,
  searchTerm,
  setSearchTerm,
  filteredItems,
  capableState,
  expandedRows,
  counterModalOpen,
  counterFormData,
  counterHasChanges,
  counterFormErrors,
  counterFormLoading,
  form,
  usesDyedUndyed = true,
  pricingUnit = null,
  alphaColumnLabel = 'Alpha',
  incotermTypeOptions,
  incotermRouteOptions,
  incotermLocationOptions,
  fobRouteOptions,
  handleInputChange,
  handleCapableChange,
  toggleRowExpand,
  handleCounterFormChange,
  handleSaveCounter,
  handleDeleteCounter,
  openCounterModal,
  closeCounterModal,
}) => {
  const alphaSuffix = pricingUnit || 'USD/BBL';
  const tableColSpan = getTableColSpan(isDeliveryEditable, usesDyedUndyed);
  const renderNumberInput = createNumberInputRenderer({
    isDeliveryEditable,
    handleInputChange,
  });
  const [selectedOriginByKey, setSelectedOriginByKey] = useState({});
  const [originModal, setOriginModal] = useState(null);

  useEffect(() => {
    const next = {};
    (filteredItems || []).forEach(item => {
      const selections =
        (Array.isArray(item?.selectedCapableLocations) &&
        item.selectedCapableLocations.length
          ? item.selectedCapableLocations
          : null) || resolveCapableLocationSelections(item);
      if (!selections.length) return;
      next[getRowKey(item)] = selections.map(loc => ({
        id: String(loc.id),
        name: loc.name || '',
      }));
    });
    setSelectedOriginByKey(prev => ({ ...prev, ...next }));
  }, [filteredItems]);

  const closeOriginModal = () => setOriginModal(null);

  const confirmOriginSelection = async () => {
    const selected = originModal?.selectedLocations || [];
    if (!selected.length) return;

    const detailId = String(originModal.detailId ?? '');
    const selectedIds = selected.map(loc => loc.id);

    setSelectedOriginByKey(prev => {
      const next = { ...prev };
      (filteredItems || []).forEach(item => {
        const key = getRowKey(item);
        const match = detailId
          ? String(item.quantityInformationDetailId ?? '') === detailId
          : key === originModal.rowKey;
        if (match) delete next[key];
      });
      return next;
    });

    await handleCapableChange(
      { target: { checked: true } },
      originModal.rowKey,
      originModal.detailId,
      selectedIds
    );
    closeOriginModal();
  };

  const onCapableToggle = (e, data, rowKey) => {
    if (hasCounterOffer(data)) return;

    if (
      e.target.checked &&
      isFobOriginDisplay(data.route, getRowOriginNames(data))
    ) {
      const current = selectedOriginByKey[rowKey] || [];
      setOriginModal({
        rowKey,
        detailId: data.quantityInformationDetailId,
        locations: getRowOriginLocations(data),
        selectedLocations: Array.isArray(current)
          ? current
          : current?.id
            ? [current]
            : [],
      });
      return;
    }

    if (!e.target.checked) {
      const detailId = String(data.quantityInformationDetailId ?? '');
      setSelectedOriginByKey(prev => {
        const next = { ...prev };
        (filteredItems || []).forEach(item => {
          const key = getRowKey(item);
          const match = detailId
            ? String(item.quantityInformationDetailId ?? '') === detailId
            : key === rowKey;
          if (match) delete next[key];
        });
        return next;
      });
    }

    handleCapableChange(e, rowKey, data.quantityInformationDetailId);
  };

  const counterByIncotermGroup = {};
  (filteredItems || []).forEach((data, index) => {
    const key = getIncotermGroupKey(data, index);
    counterByIncotermGroup[key] =
      counterByIncotermGroup[key] || hasCounterOffer(data);
  });

  const renderCounterOfferRow = (data, _index) => {
    const counterDeliveryBasis =
      data.counterDeliveryBasis ?? data.counterParcelSize ?? null;
    const deliveryBasisChanged = isCounterQuantityChanged(
      data.deliveryBasis,
      counterDeliveryBasis
    );
    const incotermRoute = formatIncotermRoute(
      data.counterIncoterm,
      data.counterRoute
    );
    const originalIncotermRoute = formatIncotermRoute(
      data.incoterm,
      data.route
    );
    const incotermRouteChanged =
      !!data.counterIncoterm &&
      isCounterValueChanged(originalIncotermRoute, incotermRoute);

    return (
      <tr className='bg-light'>
        <td colSpan={tableColSpan} className='p-0 border-0'>
          <div
            className='bg-light-subtle'
            style={{
              borderLeft: `3px solid ${COUNTER_ACCENT}`,
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <div
              className='px-2 py-2'
              style={{ width: '100%', overflowX: 'auto' }}
            >
              <table
                className='table table-sm table-borderless mb-0 align-middle w-100'
                style={{ tableLayout: 'fixed', width: '100%', minWidth: 0 }}
              >
                <thead>
                  <tr className='text-muted border-bottom'>
                    <th
                      className='fs-12 fw-semibold ps-1 pb-2'
                      style={{ width: '7%' }}
                    />
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: '11%' }}
                    >
                      Delivery Basis
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: '13%' }}
                    >
                      Incoterm - Route
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: '12%' }}
                    >
                      Loading Port
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: '12%' }}
                    >
                      Port 1
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: '12%' }}
                    >
                      Port 2
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: '10%' }}
                    >
                      Port 3
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: isDeliveryEditable ? '10%' : '13%' }}
                    >
                      {usesDyedUndyed ? 'Alpha Dyed' : alphaColumnLabel}
                    </th>
                    {usesDyedUndyed && (
                      <th
                        className='fs-12 fw-semibold pb-2 pe-2'
                        style={{ width: isDeliveryEditable ? '10%' : '13%' }}
                      >
                        Alpha Undyed
                      </th>
                    )}
                    {isDeliveryEditable && (
                      <th
                        className='fs-12 fw-semibold text-center pb-2 pe-1'
                        style={{ width: '6%' }}
                      />
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className='ps-1 py-2 align-top'>
                      <span
                        className='badge rounded-pill px-2 py-1 fw-semibold'
                        style={{
                          backgroundColor: COUNTER_ACCENT,
                          color: '#fff',
                          fontSize: '11px',
                        }}
                      >
                        Counter
                      </span>
                    </td>
                    <td
                      className={`fs-12 py-2 pe-2 align-top text-break ${deliveryBasisChanged ? 'fw-semibold' : 'fw-medium'}`}
                      style={{
                        color: deliveryBasisChanged
                          ? COUNTER_ACCENT
                          : undefined,
                        wordBreak: 'break-word',
                      }}
                    >
                      {counterDeliveryBasis != null &&
                      counterDeliveryBasis !== ''
                        ? counterDeliveryBasis
                        : '-'}
                    </td>
                    <td
                      className={`fs-12 py-2 pe-2 align-top text-break ${incotermRouteChanged ? 'fw-semibold' : ''}`}
                      style={{
                        color: incotermRouteChanged
                          ? COUNTER_ACCENT
                          : undefined,
                        wordBreak: 'break-word',
                      }}
                    >
                      {incotermRoute}
                    </td>
                    <td
                      className={`fs-12 py-2 pe-2 align-top text-break fw-medium ${
                        isCounterFobIncoterm(data) &&
                        isCounterValueChanged(
                          data.loadingPort,
                          data.counterLoadingPort
                        )
                          ? 'fw-semibold'
                          : ''
                      }`}
                      style={{
                        color:
                          isCounterFobIncoterm(data) &&
                          isCounterValueChanged(
                            data.loadingPort,
                            data.counterLoadingPort
                          )
                            ? COUNTER_ACCENT
                            : undefined,
                        wordBreak: 'break-word',
                      }}
                    >
                      {isCounterFobIncoterm(data)
                        ? data.counterLoadingPort || '-'
                        : '-'}
                    </td>
                    <td className='py-2 pe-2 align-top' style={{ minWidth: 0 }}>
                      {renderPortCell(
                        data.counterFobLocationNames?.length
                          ? null
                          : data.counterLoadingPort,
                        data.counterParcelSize,
                        {
                          portLabel: 'Location',
                          startDate: data.counterStartDate,
                          endDate: data.counterEndDate,
                          fallbackDate: data.counterAddAld,
                          locationNames: data.counterFobLocationNames,
                          route: data.counterRoute,
                          highlight: {
                            port: isCounterValueChanged(
                              data.port1 || data.loadingPort,
                              data.counterLoadingPort
                            ),
                            parcelSize: isCounterQuantityChanged(
                              data.parcelSize,
                              data.counterParcelSize
                            ),
                            date: isCounterDateChanged(
                              data.port1StartDate,
                              data.port1EndDate,
                              data.counterStartDate,
                              data.counterEndDate,
                              data.addAld,
                              data.counterAddAld
                            ),
                          },
                        }
                      )}
                    </td>
                    <td className='py-2 pe-2 align-top' style={{ minWidth: 0 }}>
                      {renderPortCell(
                        data.counterPort2,
                        data.counterParcelSize2,
                        {
                          startDate: data.counterStartDate2,
                          endDate: data.counterEndDate2,
                          fallbackDate: data.counterAddAld2,
                          highlight: {
                            port: isCounterValueChanged(
                              data.port2,
                              data.counterPort2
                            ),
                            parcelSize: isCounterQuantityChanged(
                              data.parcelSize2,
                              data.counterParcelSize2
                            ),
                            date: isCounterDateChanged(
                              data.port2StartDate,
                              data.port2EndDate,
                              data.counterStartDate2,
                              data.counterEndDate2,
                              data.addAld2,
                              data.counterAddAld2
                            ),
                          },
                        }
                      )}
                    </td>
                    <td className='py-2 pe-2 align-top' style={{ minWidth: 0 }}>
                      {renderPortCell(
                        data.counterPort3,
                        data.counterParcelSize3,
                        {
                          startDate: data.counterStartDate3,
                          endDate: data.counterEndDate3,
                          fallbackDate: data.counterAddAld3,
                          highlight: {
                            port: isCounterValueChanged(
                              data.port3,
                              data.counterPort3
                            ),
                            parcelSize: isCounterQuantityChanged(
                              data.parcelSize3,
                              data.counterParcelSize3
                            ),
                            date: isCounterDateChanged(
                              data.port3StartDate,
                              data.port3EndDate,
                              data.counterStartDate3,
                              data.counterEndDate3,
                              data.addAld3,
                              data.counterAddAld3
                            ),
                          },
                        }
                      )}
                    </td>
                    <td className='py-2 pe-2 align-top' style={{ minWidth: 0 }}>
                      {usesDyedUndyed
                        ? renderNumberInput({
                            rowKey: getRowKey(data),
                            row: data,
                            field: 'counterDyed',
                            value: data.counterDyed,
                            suffix: 'USD/bbl',
                            fullWidth: true,
                          })
                        : renderNumberInput({
                            rowKey: getRowKey(data),
                            row: data,
                            field: 'counterAlpha',
                            value: data.counterAlpha ?? data.alpha2 ?? '',
                            suffix: alphaSuffix,
                            fullWidth: true,
                          })}
                    </td>
                    {usesDyedUndyed && (
                      <td
                        className='py-2 pe-2 align-top'
                        style={{ minWidth: 0 }}
                      >
                        {renderNumberInput({
                          rowKey: getRowKey(data),
                          row: data,
                          field: 'counterUndyed',
                          value: data.counterUndyed,
                          suffix: 'USD/bbl',
                          fullWidth: true,
                        })}
                      </td>
                    )}
                    {isDeliveryEditable && (
                      <td className='text-center py-2 pe-1 align-top'>
                        <div className='d-flex justify-content-center gap-1 flex-wrap'>
                          <button
                            type='button'
                            className='btn btn-soft-primary btn-sm btn-icon'
                            title='Edit Counter Offer'
                            onClick={() => openCounterModal(data)}
                          >
                            <i className='ri-edit-line align-bottom'></i>
                          </button>
                          <button
                            type='button'
                            className='btn btn-soft-danger btn-sm btn-icon'
                            title='Delete Counter Offer'
                            onClick={() => handleDeleteCounter(data)}
                          >
                            <i className='ri-delete-bin-line align-bottom'></i>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const renderNoCounterOfferRow = () => (
    <tr className='bg-light'>
      <td colSpan={tableColSpan} className='p-0 border-0'>
        <div
          className='px-4 py-4 text-muted fs-13 text-center'
          style={{ borderLeft: `3px solid ${COUNTER_ACCENT}` }}
        >
          There is no counteroffer for the requirement mentioned above.
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <CardBody className={embedded ? 'p-3' : 'px-0'}>
        {!embedded && (
          <div className='d-flex align-items-center mb-3'>
            <div className='position-relative' style={{ width: '280px' }}>
              <i className='ri-search-line position-absolute top-50 translate-middle-y text-muted ms-3' />
              <Input
                type='text'
                placeholder='Search'
                className='ps-5 rounded-3'
                style={{ height: '36px' }}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className='table-responsive table-card border rounded-3'>
          <table
            className={`table table-hover table-centered align-middle mb-0 ${embedded ? '' : 'table-nowrap'}`}
            style={{ minWidth: embedded ? '1180px' : undefined }}
          >
            <thead className='table-light'>
              <tr>
                <th className={headerCellClass} style={{ width: '44px' }} />
                <th className={headerCellClass} style={{ minWidth: '110px' }}>
                  Delivery Basis
                </th>
                <th className={headerCellClass} style={{ minWidth: '130px' }}>
                  Incoterm - Route
                </th>
                <th className={headerCellClass} style={{ minWidth: '140px' }}>
                  Loading Port
                </th>
                <th className={headerCellClass} style={{ minWidth: '180px' }}>
                  Port 1
                </th>
                <th className={headerCellClass} style={{ minWidth: '180px' }}>
                  Port 2
                </th>
                <th className={headerCellClass} style={{ minWidth: '120px' }}>
                  Port 3
                </th>
                <th
                  className={`${headerCellClass} text-center`}
                  style={{ minWidth: '100px' }}
                >
                  Is Capable?
                </th>
                <th className={headerCellClass} style={{ minWidth: '150px' }}>
                  {usesDyedUndyed ? 'Alpha Dyed' : alphaColumnLabel}
                </th>
                {usesDyedUndyed && (
                  <th className={headerCellClass} style={{ minWidth: '150px' }}>
                    Alpha Undyed
                  </th>
                )}
                {isDeliveryEditable && (
                  <th
                    className={`${headerCellClass} text-center`}
                    style={{ minWidth: '90px' }}
                  >
                    Counter Offer
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={tableColSpan} className='text-center py-4'>
                    <Spinner size='sm' color='primary' />
                  </td>
                </tr>
              ) : filteredItems.length > 0 ? (
                filteredItems.map((data, index) => {
                  const rowKey = getRowKey(data);
                  const isCapable = capableState[rowKey] || false;
                  const isExpanded = expandedRows[rowKey];
                  const showCounter = hasCounterOffer(data);
                  const groupCountered =
                    !!counterByIncotermGroup[getIncotermGroupKey(data, index)];

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className={isExpanded ? 'border-bottom-1' : undefined}
                        style={
                          isExpanded || showCounter
                            ? { borderLeft: `3px solid ${COUNTER_ACCENT}` }
                            : undefined
                        }
                      >
                        <td className={cellClass}>
                          <button
                            type='button'
                            className='btn btn-link btn-sm p-0 text-muted'
                            onClick={() => toggleRowExpand(rowKey)}
                          >
                            <i
                              className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line fs-16`}
                            />
                          </button>
                        </td>
                        <td className={`${cellClass} fw-medium fs-13`}>
                          {data.deliveryBasis || `D${index + 1}`}
                        </td>
                        <td className={`${cellClass} fs-13`}>
                          {formatIncotermRoute(data.incoterm, data.route)}
                        </td>
                        <td className={`${cellClass} fs-13 fw-medium`}>
                          {resolveLoadingPortLabel(
                            data,
                            selectedOriginByKey[rowKey]
                          )}
                        </td>
                        <td className={cellClass}>
                          {renderPortCell(
                            data.port1 || data.loadingPort,
                            data.parcelSize,
                            {
                              portLabel: 'Location',
                              startDate: data.port1StartDate,
                              endDate: data.port1EndDate,
                              fallbackDate: data.addAld,
                              locationNames: data.fobLocationNames,
                              route: data.route,
                            }
                          )}
                        </td>
                        <td className={cellClass}>
                          {renderPortCell(data.port2, data.parcelSize2, {
                            startDate: data.port2StartDate,
                            endDate: data.port2EndDate,
                            fallbackDate: data.addAld2,
                          })}
                        </td>
                        <td className={cellClass}>
                          {renderPortCell(data.port3, data.parcelSize3, {
                            startDate: data.port3StartDate,
                            endDate: data.port3EndDate,
                            fallbackDate: data.addAld3,
                          })}
                        </td>
                        <td className={`${cellClass} text-center`}>
                          {!isDeliveryEditable ? (
                            isCapable ? (
                              <Badge
                                color='primary'
                                className='px-2 py-1 fs-12 fw-medium rounded-pill'
                              >
                                Compliant
                              </Badge>
                            ) : groupCountered ? (
                              <span
                                className='badge rounded-pill px-2 py-1 fs-12 fw-medium'
                                style={{
                                  backgroundColor: COUNTER_ACCENT,
                                  color: '#fff',
                                }}
                              >
                                Countered
                              </span>
                            ) : (
                              <Badge
                                color='secondary'
                                className='px-2 py-1 fs-12 fw-medium rounded-pill'
                              >
                                Not Offered
                              </Badge>
                            )
                          ) : (
                            <div className='form-check form-switch d-flex justify-content-center mb-0'>
                              <Input
                                className='form-check-input'
                                type='checkbox'
                                role='switch'
                                checked={showCounter ? false : isCapable}
                                disabled={showCounter}
                                onChange={e => onCapableToggle(e, data, rowKey)}
                              />
                            </div>
                          )}
                        </td>
                        <td className={cellClass}>
                          {usesDyedUndyed
                            ? renderNumberInput({
                                rowKey,
                                row: data,
                                field: 'unitBbl',
                                value: data.dyed ?? data.unitBbl,
                                disabled: !isCapable,
                                suffix: 'USD/bbl',
                              })
                            : renderNumberInput({
                                rowKey,
                                row: data,
                                field: 'alpha',
                                value: data.alpha || '',
                                disabled: !isCapable,
                                suffix: alphaSuffix,
                              })}
                        </td>
                        {usesDyedUndyed && (
                          <td className={cellClass}>
                            {renderNumberInput({
                              rowKey,
                              row: data,
                              field: 'cbm',
                              value: data.undyed ?? data.cbm,
                              disabled: !isCapable,
                              suffix: 'USD/bbl',
                            })}
                          </td>
                        )}
                        {isDeliveryEditable && (
                          <td className={`${cellClass} text-center`}>
                            {!showCounter && !isCapable ? (
                              <button
                                type='button'
                                className='btn btn-soft-primary btn-sm btn-icon'
                                title='Counter Offer'
                                onClick={() => openCounterModal(data)}
                              >
                                <i className='ri-edit-line align-bottom'></i>
                              </button>
                            ) : null}
                          </td>
                        )}
                      </tr>
                      {isExpanded &&
                        (showCounter
                          ? renderCounterOfferRow(data, index)
                          : renderNoCounterOfferRow())}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={tableColSpan}
                    className='text-center text-muted py-4'
                  >
                    {searchTerm
                      ? 'No data matching your search'
                      : 'No data available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardBody>

      <CounterOfferModal
        isOpen={counterModalOpen}
        toggle={closeCounterModal}
        formData={counterFormData}
        saveDisabled={!counterHasChanges}
        errors={counterFormErrors}
        onFormChange={handleCounterFormChange}
        onSave={handleSaveCounter}
        loading={counterFormLoading}
        form={form}
        incotermTypeOptions={incotermTypeOptions}
        incotermRouteOptions={incotermRouteOptions}
        incotermLocationOptions={incotermLocationOptions}
        fobRouteOptions={fobRouteOptions}
      />
      <SelectOriginLocationModal
        isOpen={Boolean(originModal)}
        toggle={closeOriginModal}
        locations={originModal?.locations ?? []}
        selectedIds={(originModal?.selectedLocations || []).map(loc => loc.id)}
        onToggle={loc =>
          setOriginModal(prev => {
            if (!prev) return prev;
            const current = prev.selectedLocations || [];
            const exists = current.some(item => item.id === loc.id);
            return {
              ...prev,
              selectedLocations: exists
                ? current.filter(item => item.id !== loc.id)
                : [...current, loc],
            };
          })
        }
        onConfirm={confirmOriginSelection}
      />
    </>
  );
};

export default ModelDeliveryTableNonLpg;
